// ResetPassword.js
import React, { useState, useEffect } from "react";
import { Button, Form, Input, message, Card, Progress } from "antd";
import { ArrowLeft, Lock, Shield, CheckCircle, XCircle } from "lucide-react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { API_METHODS } from "../../constants";
import useApi from "../../hooks/useApi";
import HeaderComponent from "./Home/HeaderComponent";
import Footer from "./Home/Footer";
import logo from "../../assets/logo.png";

const ResetPassword = () => {
  const { token } = useParams();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const navigate = useNavigate();

  // API hook
  const [resetResponse, sendResetRequest] = useApi();

  // Handle form submission
  const handleSubmit = (values) => {
    sendResetRequest(
      {
        token: token,
        password: values.password,
      },
      API_METHODS.POST,
      "/user/auth/reset-password"
    );
  };

  // Handle API response
  useEffect(() => {
    if (resetResponse.data && !resetResponse.isLoading) {
      setResetSuccess(true);
      messageApi.open({
        type: "success",
        content: "Password reset successfully! Redirecting to login...",
        duration: 4,
      });

      // Start countdown for auto-redirect
      startCountdown();
    } else if (resetResponse.error) {
      messageApi.open({
        type: "error",
        content:
          resetResponse.error || "Failed to reset password. Please try again.",
        duration: 4,
      });
    }
  }, [resetResponse, messageApi]);

  const startCountdown = () => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/login");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Password strength checker
  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    if (/[@$!%*?&]/.test(password)) strength += 25;
    return strength;
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setPasswordStrength(checkPasswordStrength(password));
  };

  const getStrengthColor = () => {
    if (passwordStrength < 50) return "#ff4d4f";
    if (passwordStrength < 75) return "#faad14";
    return "#52c41a";
  };

  const getStrengthText = () => {
    if (passwordStrength < 50) return "Weak";
    if (passwordStrength < 75) return "Good";
    return "Strong";
  };

  // Check if token exists
  if (!token) {
    return (
      <>
        <HeaderComponent />
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 py-12 px-4">
          <div className="container mx-auto max-w-md">
            <Card className="rounded-2xl shadow-xl border border-orange-100">
              <div className="text-center">
                <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  Invalid Reset Link
                </h2>
                <p className="text-gray-600 mb-4">
                  The password reset link is invalid or malformed.
                </p>
                <Link to="/forgot-password">
                  <Button
                    type="primary"
                    className="bg-gradient-to-r from-orange-500 to-red-500 border-none"
                  >
                    Request New Reset Link
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      {contextHolder}
      <HeaderComponent />

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 py-12 px-4">
        <div className="container mx-auto max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-12 h-12 rounded-full overflow-hidden">
                <img
                  src={logo}
                  alt="MahalaxmiPuja Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  MahalaxmiPuja
                </h1>
                <p className="text-orange-600 font-medium text-sm">
                  Shree Mahalaxmi Ambabai, Kolhapur
                </p>
              </div>
            </div>
          </div>

          {!resetSuccess ? (
            // Reset Password Form
            <Card className="rounded-2xl shadow-xl border border-orange-100">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-100 to-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="h-8 w-8 text-orange-500" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Reset Your Password
                </h2>
                <p className="text-gray-600">
                  Enter your new password below to secure your account.
                </p>
              </div>

              <Form form={form} onFinish={handleSubmit} layout="vertical">
                <Form.Item
                  label="New Password"
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your new password",
                    },
                    {
                      pattern:
                        /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,15}$/,
                      message:
                        "Password must be 8-15 characters with uppercase, digit, and special character (@$!%*?&)",
                    },
                  ]}
                >
                  <Input.Password
                    size="large"
                    placeholder="Enter your new password"
                    prefix={<Lock className="h-4 w-4 text-gray-400" />}
                    onChange={handlePasswordChange}
                  />
                </Form.Item>

                {/* Password Strength Indicator */}
                {passwordStrength > 0 && (
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">
                        Password Strength:
                      </span>
                      <span
                        className="text-sm font-medium"
                        style={{ color: getStrengthColor() }}
                      >
                        {getStrengthText()}
                      </span>
                    </div>
                    <Progress
                      percent={passwordStrength}
                      strokeColor={getStrengthColor()}
                      trailColor="#f5f5f5"
                      strokeWidth={8}
                      showInfo={false}
                    />
                  </div>
                )}

                <Form.Item
                  label="Confirm New Password"
                  name="confirmPassword"
                  dependencies={["password"]}
                  rules={[
                    {
                      required: true,
                      message: "Please confirm your new password",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Passwords do not match")
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    size="large"
                    placeholder="Confirm your new password"
                    prefix={<Lock className="h-4 w-4 text-gray-400" />}
                  />
                </Form.Item>

                {/* Password Requirements */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-blue-800 mb-2 flex items-center">
                    <Shield className="h-4 w-4 mr-2" />
                    Password Requirements:
                  </h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• 8-15 characters long</li>
                    <li>• At least one uppercase letter</li>
                    <li>• At least one number</li>
                    <li>• At least one special character (@$!%*?&)</li>
                  </ul>
                </div>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={resetResponse.isLoading}
                    block
                    className="h-12 bg-gradient-to-r from-orange-500 to-red-500 border-none hover:from-orange-600 hover:to-red-600 font-medium"
                    icon={<Shield className="h-4 w-4" />}
                  >
                    Reset Password
                  </Button>
                </Form.Item>
              </Form>

              <div className="text-center">
                <Link
                  to="/login"
                  className="flex items-center justify-center text-orange-600 hover:text-orange-700 font-medium text-sm"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to Login
                </Link>
              </div>
            </Card>
          ) : (
            // Reset Success
            <Card className="rounded-2xl shadow-xl border border-orange-100">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  🎉 Password Reset Successfully!
                </h2>

                <p className="text-gray-600 mb-6">
                  Your password has been updated. You can now login with your
                  new password.
                </p>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-green-800 mb-2">
                    What's Next?
                  </h3>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>✓ Your password is now secure</li>
                    <li>✓ You can login immediately</li>
                    <li>✓ Consider saving it in a password manager</li>
                  </ul>
                </div>

                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-700 text-sm mb-2">
                      Redirecting to login in <strong>{countdown}</strong>{" "}
                      seconds...
                    </p>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-1000"
                        style={{ width: `${((5 - countdown) / 5) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  <Button
                    type="primary"
                    size="large"
                    onClick={() => navigate("/login")}
                    block
                    className="bg-gradient-to-r from-orange-500 to-red-500 border-none hover:from-orange-600 hover:to-red-600"
                  >
                    Login Now
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default ResetPassword;
